import cx from "classnames";
import isEmpty from "lodash/isEmpty";
import isNull from "lodash/isNull";

import s from "./StripePaymentForm.module.scss";
import { memo, useCallback, useEffect, useState } from "react";
import { StripePaymentElementOptions } from "@stripe/stripe-js";
import {
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";

import { IonToast } from "@ionic/react";
import { useFormsStore } from "../../../../Common/store/common";
import useWebSignupStore, {
  defaultValidatePaymentElement,
  PAYMENT_ELEMENT_NOT_READY_MESSAGE,
} from "../store";
import { getErrorMsg } from "../../../../Common/utils/common";
import { bugNotif } from "../../../api/bugsnag";
import { WEB_DOWNLOAD_APP } from "../../../constants/routes";
import { getDownloadLandingVersion } from "../../../utils/redirectDownload";
import { getSignupStripeReturnOrigin } from "../../../utils/signupStripeReturnUrl";
import { appendWebLogRecordQueryParam } from "../../../utils/webLogRocketRecord";
import {
  trackSignupCheckout,
  trackSignupAddPaymentInfo,
} from "../../../services/marketing";
import { appendApplovinClickIdsToUrl } from "../../../services/marketing/axon";
import { trackWebSignUpStep } from "../../../services/user";
import { PW_WEB_TEST_CONFIG } from "../../../../Common/testConstants";
import { logWebSignupTimingOnce } from "../signupTiming";

const ttqEventSent = false;
const StripePaymentForm = () => {
  const stripe = useStripe();
  const elements = useElements();
  const setFormLoading = useFormsStore((state) => state.setFormLoading);
  const [error, setError] = useState("");

  const PAYMENT_ELEMENT_OPTIONS = {
    billingDetails: {
      address: "never",
      postalCode: "never",
    },

    terms: {
      card: "never",
      googlePay: "never",
      applePay: "never",
    },
  } as StripePaymentElementOptions;
  const { setSubmitPayment, setValidatePaymentElement } = useWebSignupStore();

  const validatePaymentElement = useCallback(async () => {
    if (!stripe) {
      return PAYMENT_ELEMENT_NOT_READY_MESSAGE;
    }

    if (isNull(elements)) {
      return PAYMENT_ELEMENT_NOT_READY_MESSAGE;
    }

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        return submitError.message || "Please complete your payment details.";
      }
      return null;
    } catch {
      return PAYMENT_ELEMENT_NOT_READY_MESSAGE;
    }
  }, [stripe, elements]);

  useEffect(() => {
    setValidatePaymentElement(validatePaymentElement);
    return () => {
      setValidatePaymentElement(defaultValidatePaymentElement);
    };
  }, [setValidatePaymentElement, validatePaymentElement]);

  const submitPaymentForm = useCallback(async () => {
    const {
      userId,
      selectedSubscription,
      stripeClientSecretId,
      firstName,
      lastName,
      phoneNumber,
      emailAddress,
      validStripeCoupon,
    } = useWebSignupStore.getState();

    if (selectedSubscription === undefined || isNull(selectedSubscription)) {
      return setError("No selected plan found. Please try again.");
    }

    if (stripeClientSecretId === undefined) {
      return setError("Something went wrong. Please try again.");
    }

    const leftoverAlreadyPaid = isNull(stripeClientSecretId);

    if (!leftoverAlreadyPaid) {
      if (!stripe) {
        return setError("Form is not yet ready. Please try again.");
      }

      if (isNull(elements)) {
        return setError("Something went wrong. Please try again.");
      }
    }

    try {
      setFormLoading(true);

      const tiktokSource = new URLSearchParams(window.location.search).get(
        "ttclid"
      );

      const promoCode = new URLSearchParams(window.location.search).get(
        "promo"
      );
      const gtId = new URLSearchParams(window.location.search).get("gtId");
      const wldpVersionParam = new URLSearchParams(window.location.search).get(
        "wldpVersion"
      );
      const wldpVersion = getDownloadLandingVersion(wldpVersionParam);
      const promo =
        !isNull(promoCode) && !isEmpty(promoCode)
          ? promoCode.toUpperCase().trim()
          : null;
      const parsedUrl = new URL(window.location.href);
      const returnOrigin = getSignupStripeReturnOrigin(parsedUrl);
      const paramFullName = `${firstName} ${lastName}`.split(" ").join("_");
      const signUpVersion = 9;
      const entriesPerMonthParam =
        selectedSubscription.entriesPerMonth != null
          ? `&entriesPerMonth=${selectedSubscription.entriesPerMonth}`
          : "";
      let returnUrl = `${returnOrigin}${WEB_DOWNLOAD_APP}/?userId=${userId}&pid=${selectedSubscription.docId}&signedUpFullName=${paramFullName}&emailAddress=${encodeURIComponent(emailAddress)}&phoneNumber=${encodeURIComponent(phoneNumber)}&promoCode=${promo || "null"}&signUpVersion=${signUpVersion}&productName=${selectedSubscription.name}&wl-pl=true&wldpVersion=${wldpVersion}${entriesPerMonthParam}${gtId && !isEmpty(gtId) ? `&gtId=${gtId}` : ""}`;

      if (!isNull(tiktokSource)) {
        returnUrl = `${returnUrl}&ttclid=${tiktokSource}`;
      }

      returnUrl = appendApplovinClickIdsToUrl(returnUrl);

      returnUrl = appendWebLogRecordQueryParam(returnUrl);

      await trackSignupCheckout({
        emailAddress,
        phoneNumber,
        subscription: selectedSubscription,
        tiktokSource,
        validStripeCoupon,
      });

      await trackWebSignUpStep({
        emailAddress,
        step: "subscription",
        version: 9,
        subscription: selectedSubscription.name,
        promoCode: promo,
        revenue: null,
      });

      if (!stripeClientSecretId) {
        window.location.assign(returnUrl);
        return;
      }

      // Confirm the Intent using the details collected by the Payment Element
      const { error: errorPayment } = await stripe!.confirmPayment({
        elements: elements!,
        clientSecret: stripeClientSecretId,
        confirmParams: {
          return_url: returnUrl,
        },
      });

      if (errorPayment) {
        setError(errorPayment.message || "");
      }

      setFormLoading(false);
    } catch (error) {
      setFormLoading(false);

      const { errorMsg, rawErrorMsg } = getErrorMsg(error);
      setError(errorMsg);
      bugNotif("submitPaymentForm", rawErrorMsg);
    }
  }, [stripe, elements, setFormLoading]);

  useEffect(() => {
    setSubmitPayment(submitPaymentForm);
    return () => {
      setSubmitPayment(async () => {});
    };
  }, [setSubmitPayment, submitPaymentForm]);

  const onCompletePaymentInfo = async (paymentMethod: string) => {
    const selectedSubscription =
      useWebSignupStore.getState().selectedSubscription;

    if (selectedSubscription === undefined || isNull(selectedSubscription)) {
      return false;
    }

    await trackSignupAddPaymentInfo({ subscription: selectedSubscription });
  };

  return (
    <div
      className={s.paymentContainer}
      data-testid={PW_WEB_TEST_CONFIG.stripeForm.paymentContainer}
    >
      <PaymentElement
        onReady={() => {
          logWebSignupTimingOnce(
            "payment-element-ready",
            "payment form: Payment Element visible/ready"
          );
        }}
        onChange={(event) => {
          if (event.complete) {
            onCompletePaymentInfo(event.value.type);
          }
        }}
        options={PAYMENT_ELEMENT_OPTIONS}
      />

      <IonToast
        isOpen={!isEmpty(error)}
        color="danger"
        message={error}
        duration={5000}
      />
    </div>
  );
};

export default memo(StripePaymentForm);
